// src/modules/operaciones/inventarios/dashboard/PanelFabricantes.js
//
// Pestaña "Fabricantes" del dashboard de mermas:
//   1. Treemap de pérdida bruta por fabricante (tamaño y tono de rojo ∝ pérdida)
//   2. Top 10 fabricantes por venta
//   3. Top 10 fabricantes por merma neta (los más negativos)
//   4. Tabla ordenable con venta, merma neta, % merma/venta y artículos
//
// USO
//   <PanelFabricantes analisis={d.analisis} />
//
// Espera analisis.fabricantes = { lista, topVenta, topMerma, treemap } (salida de analizarMermas).
//   - pérdida bruta = faltantes de la merma desconocida + merma conocida (no resta sobrantes)
//   - merma neta    = merma desconocida + merma conocida (los sobrantes compensan)

import React, { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, Tooltip, Treemap, XAxis, YAxis } from "recharts";
import { ArrowDown, ArrowUp, BarChart3 } from "lucide-react";

import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import {
  COLORES_SEMANTICOS,
  fmtCompacto,
  fmtMoneda,
  fmtNumero,
  fmtPorcentaje,
  mezclarColores,
  truncar,
} from "../utils/informesComun";

const TICK = { fontSize: 11, fill: COLORES_SEMANTICOS.texto };
const dominioConAire = [(min) => Math.min(min * 1.25, 0), (max) => Math.max(max * 1.25, 0)];

/* ========================================================================== */
/* 1. Treemap de pérdida bruta                                                */
/* ========================================================================== */

const ROJO_CLARO = "#fecaca";
const ROJO_OSCURO = "#b91c1c";

// Un rectángulo del treemap. Recharts le entrega x, y, width, height, name y depth;
// maxSize y porNombre los agregamos nosotros.
function CeldaTreemap(props) {
  const { x, y, width, height, name, depth, maxSize, porNombre } = props;
  if (!name || depth === 0 || !(width > 0) || !(height > 0)) return null;

  const size = porNombre.get(name)?.size ?? 0;
  // Raíz cuadrada: así los fabricantes pequeños no quedan todos del mismo rojo pálido
  const t = maxSize > 0 ? Math.sqrt(size / maxSize) : 0;
  const fondo = mezclarColores(ROJO_CLARO, ROJO_OSCURO, t);
  const texto = t > 0.45 ? "#ffffff" : "#7f1d1d";

  const cabeTexto = width > 56 && height > 26;
  const cabeValor = cabeTexto && height > 44;
  const maxCaracteres = Math.max(Math.floor((width - 12) / 6.5), 3);

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fondo} stroke="#fff" strokeWidth={2} rx={3} />
      {cabeTexto && (
        <text x={x + 6} y={y + 16} fontSize={11} fontWeight={600} fill={texto}>
          {truncar(name, maxCaracteres)}
        </text>
      )}
      {cabeValor && (
        <text x={x + 6} y={y + 31} fontSize={11} fill={texto} fillOpacity={0.9}>
          {fmtCompacto(size)}
        </text>
      )}
    </g>
  );
}

// Tooltip del treemap: se arma un payload propio para reutilizar ChartTooltip
function TooltipTreemap({ active, payload, porNombre }) {
  if (!active || !payload?.length) return null;
  const nombre = payload[0]?.payload?.name ?? payload[0]?.name;
  const d = porNombre.get(nombre);
  if (!d) return null;
  return (
    <ChartTooltip
      active
      titulo={d.name}
      colorearSigno
      payload={[
        { dataKey: "perdidaBruta", name: "Pérdida bruta", value: d.perdidaBruta, color: COLORES_SEMANTICOS.perdida },
        { dataKey: "venta", name: "Venta", value: d.venta, color: COLORES_SEMANTICOS.venta },
        { dataKey: "perdidaBrutaPct", name: "Pérdida / venta", value: d.perdidaBrutaPct, color: COLORES_SEMANTICOS.neutro },
      ]}
    />
  );
}

function GraficoTreemap({ datos, maxSize, porNombre }) {
  return (
    <Treemap
      data={datos}
      dataKey="size"
      nameKey="name"
      aspectRatio={4 / 3}
      isAnimationActive={false}
      content={<CeldaTreemap maxSize={maxSize} porNombre={porNombre} />}
    >
      <Tooltip content={<TooltipTreemap porNombre={porNombre} />} />
    </Treemap>
  );
}

/* ========================================================================== */
/* 2 y 3. Barras horizontales (Top 10)                                        */
/* ========================================================================== */

function BarrasHorizontales({ datos, clave, nombreSerie, colorDe, filasExtra }) {
  // Etiqueta de valor al extremo de la barra (sirve para negativas y positivas)
  const etiqueta = (props) => {
    const { x, y, width, height, index } = props;
    const d = datos[index];
    if (!d || x === undefined) return null;
    const izquierda = Math.min(x, x + width);
    const derecha = Math.max(x, x + width);
    const negativo = d[clave] < 0;
    return (
      <text
        x={negativo ? izquierda - 6 : derecha + 6}
        y={y + height / 2}
        textAnchor={negativo ? "end" : "start"}
        dominantBaseline="middle"
        fontSize={11}
        fontWeight={600}
        fill="#374151"
      >
        {fmtCompacto(d[clave])}
      </text>
    );
  };

  return (
    <BarChart data={datos} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
      <CartesianGrid horizontal={false} stroke={COLORES_SEMANTICOS.grilla} />
      <XAxis
        type="number"
        domain={dominioConAire}
        tickFormatter={(v) => fmtCompacto(v)}
        tick={TICK}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        type="category"
        dataKey="nombre"
        width={150}
        tick={{ ...TICK, fill: "#374151" }}
        tickFormatter={(v) => truncar(v, 24)}
        tickLine={false}
      />
      <ReferenceLine x={0} stroke="#9ca3af" />
      <Tooltip
        cursor={{ fill: "rgba(0,0,0,0.04)" }}
        content={<ChartTooltip formato="moneda" colorearSigno filasExtra={filasExtra} />}
      />
      <Bar dataKey={clave} name={nombreSerie} maxBarSize={24} label={etiqueta}>
        {datos.map((d) => (
          <Cell key={d.nombre} fill={colorDe(d)} />
        ))}
      </Bar>
    </BarChart>
  );
}

/* ========================================================================== */
/* 4. Tabla ordenable                                                         */
/* ========================================================================== */

const COLUMNAS_TABLA = [
  { id: "nombre", etiqueta: "Fabricante", alinear: "left" },
  { id: "venta", etiqueta: "Venta", alinear: "right" },
  { id: "neta", etiqueta: "Merma neta", alinear: "right" },
  { id: "mermaPct", etiqueta: "% Merma/Venta", alinear: "right" },
  { id: "articulos", etiqueta: "Artículos", alinear: "right" },
];

function TablaFabricantes({ lista, alto }) {
  const [orden, setOrden] = useState({ id: "venta", dir: "desc" });

  const filas = useMemo(() => {
    const factor = orden.dir === "asc" ? 1 : -1;
    return [...lista].sort((a, b) => {
      const va = a[orden.id];
      const vb = b[orden.id];
      // Los valores vacíos (% sin venta) siempre van al final
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      if (typeof va === "string") return factor * va.localeCompare(vb, "es");
      return factor * (va - vb);
    });
  }, [lista, orden]);

  const cambiarOrden = (id) =>
    setOrden((prev) =>
      prev.id === id ? { id, dir: prev.dir === "asc" ? "desc" : "asc" } : { id, dir: id === "nombre" ? "asc" : "desc" }
    );

  const colorValor = (v) =>
    typeof v === "number" && v < 0 ? "text-red-600" : typeof v === "number" && v > 0 ? "text-emerald-600" : "text-gray-700";

  return (
    <div className="overflow-auto border border-gray-100 rounded-lg" style={{ maxHeight: alto }}>
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-gray-50 z-10">
          <tr>
            {COLUMNAS_TABLA.map((c) => {
              const activa = orden.id === c.id;
              return (
                <th
                  key={c.id}
                  scope="col"
                  aria-sort={activa ? (orden.dir === "asc" ? "ascending" : "descending") : "none"}
                  className={`px-3 py-2 text-xs font-semibold text-gray-600 whitespace-nowrap ${
                    c.alinear === "right" ? "text-right" : "text-left"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => cambiarOrden(c.id)}
                    className="inline-flex items-center gap-1 hover:text-gray-900 focus:outline-none focus:underline"
                  >
                    {c.etiqueta}
                    {activa && (orden.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {filas.map((f) => (
            <tr key={f.nombre} className="hover:bg-gray-50">
              <td className="px-3 py-1.5 text-gray-800 max-w-[16rem] truncate" title={f.nombre}>
                {f.nombre}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 whitespace-nowrap">{fmtMoneda(f.venta)}</td>
              <td className={`px-3 py-1.5 text-right tabular-nums font-medium whitespace-nowrap ${colorValor(f.neta)}`}>
                {fmtMoneda(f.neta)}
              </td>
              <td className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap ${colorValor(f.mermaPct)}`}>
                {f.mermaPct === null ? "-" : fmtPorcentaje(f.mermaPct, 2)}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">{fmtNumero(f.articulos)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ========================================================================== */
/* Panel                                                                      */
/* ========================================================================== */

function SinDatos() {
  return (
    <div className="flex flex-col items-center justify-center text-center bg-white border border-dashed border-gray-300 rounded-xl py-16 px-4">
      <BarChart3 className="w-10 h-10 text-gray-300 mb-3" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-700">No hay datos de fabricantes</p>
      <p className="text-xs text-gray-500 mt-1">Ajusta los filtros o revisa que el archivo tenga la columna de fabricante.</p>
    </div>
  );
}

export function PanelFabricantes({ analisis }) {
  const f = analisis?.fabricantes;
  const lista = useMemo(() => f?.lista || [], [f]);
  const topVenta = useMemo(() => f?.topVenta || [], [f]);
  const topMerma = useMemo(() => f?.topMerma || [], [f]);
  const treemap = useMemo(() => f?.treemap || [], [f]);

  const porNombre = useMemo(() => new Map(treemap.map((t) => [t.name, t])), [treemap]);
  const maxSize = treemap.length > 0 ? treemap[0].size : 0; // ya viene ordenado de mayor a menor

  // Qué tanto se concentra la pérdida en los primeros fabricantes
  const notaTreemap = useMemo(() => {
    const total = treemap.reduce((s, t) => s + t.size, 0);
    if (total <= 0) return null;
    const n = Math.min(5, treemap.length);
    const top = treemap.slice(0, n).reduce((s, t) => s + t.size, 0);
    return `${n === 1 ? "El primer fabricante concentra" : `Los ${n} primeros fabricantes concentran`} el ${fmtPorcentaje((top / total) * 100, 0)} de la pérdida bruta (${fmtCompacto(-total)}).`;
  }, [treemap]);

  if (!f || lista.length === 0) return <SinDatos />;

  const alturaTop = Math.max(320, Math.max(topVenta.length, topMerma.length) * 36 + 60);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartCard
        className="lg:col-span-2"
        titulo="Pérdida bruta por fabricante"
        subtitulo={`Tamaño y tono de rojo según la pérdida (faltantes + merma conocida) · ${fmtNumero(treemap.length)} fabricantes con pérdida`}
        vacio={treemap.length === 0}
        mensajeVacio="Ningún fabricante tiene pérdida con los filtros actuales."
        pie={notaTreemap}
        height={380}
      >
        <GraficoTreemap datos={treemap} maxSize={maxSize} porNombre={porNombre} />
      </ChartCard>

      <ChartCard
        titulo="Top fabricantes por venta"
        subtitulo="Los que más venden ($)"
        vacio={topVenta.length === 0}
        height={alturaTop}
      >
        <BarrasHorizontales
          datos={topVenta}
          clave="venta"
          nombreSerie="Venta"
          colorDe={() => COLORES_SEMANTICOS.venta}
          filasExtra={(d) => [
            { etiqueta: "Participación", valor: d.participacionVenta, formato: "porcentaje" },
            { etiqueta: "Merma neta", valor: d.neta, formato: "moneda", color: COLORES_SEMANTICOS.perdida },
          ]}
        />
      </ChartCard>

      <ChartCard
        titulo="Top fabricantes por merma neta"
        subtitulo="Los de merma más negativa ($)"
        vacio={topMerma.length === 0}
        mensajeVacio="Ningún fabricante tiene merma neta negativa."
        height={alturaTop}
      >
        <BarrasHorizontales
          datos={topMerma}
          clave="neta"
          nombreSerie="Merma neta"
          colorDe={() => COLORES_SEMANTICOS.perdida}
          filasExtra={(d) => [
            { etiqueta: "Venta", valor: d.venta, formato: "moneda", color: COLORES_SEMANTICOS.venta },
            { etiqueta: "Merma / venta", valor: d.mermaPct, formato: "porcentaje" },
          ]}
        />
      </ChartCard>

      <ChartCard
        className="lg:col-span-2"
        titulo="Detalle por fabricante"
        subtitulo="Haz clic en un encabezado para ordenar"
        responsive={false}
        height="auto"
      >
        {({ pantallaCompleta }) => <TablaFabricantes lista={lista} alto={pantallaCompleta ? "65vh" : "20rem"} />}
      </ChartCard>
    </div>
  );
}

export default PanelFabricantes;